import { useEffect, useState } from 'react';
import { MessageSquare, Clock, Users, CheckCircle } from 'lucide-react';
import StatsCard from '../components/StatsCard';
import LeaderboardTable from '../components/LeaderboardTable';
import { PieChart, Pie, Cell, ResponsiveContainer, Legend, Tooltip } from 'recharts';

interface ChatStats {
  statusCounts: { [key: string]: number };
  sellerCounts: { [key: string]: number };
  avgResponseTime: number;
  totalChats: number;
}

export default function EstatisticasChats() {
  const [stats, setStats] = useState<ChatStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({
    start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
    end_date: new Date().toISOString().split('T')[0]
  });

  useEffect(() => {
    loadStats();
  }, [filter]);

  const loadStats = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3001';
      
      const params = new URLSearchParams();
      if (filter.start_date) params.append('start_date', filter.start_date);
      if (filter.end_date) params.append('end_date', filter.end_date);

      const res = await fetch(`${apiUrl}/api/stats/chats?${params}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      const data = await res.json();
      
      if (data.success) {
        setStats(data.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas de chats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-600">Carregando estatísticas...</p>
        </div>
      </div>
    );
  }

  const statusColors: { [key: string]: string } = {
    active: '#10b981',
    pending: '#f59e0b',
    closed: '#6b7280',
    transferred: '#3b82f6'
  };

  const statusLabels: { [key: string]: string } = {
    active: 'Ativos',
    pending: 'Pendentes',
    closed: 'Fechados',
    transferred: 'Transferidos'
  };

  const statusData = Object.entries(stats?.statusCounts || {}).map(([status, count]) => ({
    name: statusLabels[status] || status,
    value: count,
    color: statusColors[status] || '#6b7280'
  }));

  const sellerData = Object.entries(stats?.sellerCounts || {}).map(([name, count], index) => ({
    id: `seller-${index}`,
    name,
    value: count
  }));

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-3xl font-bold text-gray-900">Estatísticas de Chats</h1>
        <p className="text-gray-600">Análise detalhada de atendimentos</p>
      </div>

      {/* Filtros */}
      <div className="bg-white rounded-lg shadow p-6 mb-6">
        <h2 className="text-xl font-semibold mb-4">Filtrar Período</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Início</label>
            <input
              type="date"
              value={filter.start_date}
              onChange={(e) => setFilter({ ...filter, start_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Data Fim</label>
            <input
              type="date"
              value={filter.end_date}
              onChange={(e) => setFilter({ ...filter, end_date: e.target.value })}
              className="w-full border border-gray-300 rounded-md px-3 py-2"
            />
          </div>

          <div className="flex items-end">
            <button
              onClick={() => setFilter({
                start_date: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000).toISOString().split('T')[0],
                end_date: new Date().toISOString().split('T')[0]
              })}
              className="w-full bg-gray-500 text-white px-4 py-2 rounded-md hover:bg-gray-600"
            >
              Últimos 30 dias
            </button>
          </div>
        </div>
      </div>

      {/* Cards de Estatísticas */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
        <StatsCard
          title="Total de Chats"
          value={stats?.totalChats || 0}
          icon={MessageSquare}
          iconColor="text-blue-500"
        />
        
        <StatsCard
          title="Tempo Médio de Resposta"
          value={`${stats?.avgResponseTime || 0} min`}
          icon={Clock}
          iconColor="text-orange-500"
        />
        
        <StatsCard
          title="Chats Ativos"
          value={stats?.statusCounts?.active || 0}
          icon={CheckCircle}
          iconColor="text-green-500"
        />
        
        <StatsCard
          title="Vendedores Ativos"
          value={Object.keys(stats?.sellerCounts || {}).length}
          icon={Users}
          iconColor="text-purple-500"
        />
      </div>

      {/* Gráficos */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        {/* Gráfico de Pizza - Status */}
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Chats por Status</h3>
          {statusData.length > 0 ? (
            <ResponsiveContainer width="100%" height={300}>
              <PieChart>
                <Pie
                  data={statusData}
                  cx="50%"
                  cy="50%"
                  labelLine={false}
                  label={({ name, percent }: any) => `${name}: ${(percent * 100).toFixed(0)}%`}
                  outerRadius={80}
                  fill="#8884d8"
                  dataKey="value"
                >
                  {statusData.map((entry, index) => (
                    <Cell key={`cell-${index}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-[300px] flex items-center justify-center text-gray-500">
              Nenhum dado disponível
            </div>
          )}
        </div>

        {/* Ranking de Vendedores */}
        <LeaderboardTable
          title="Ranking de Atendimentos"
          data={sellerData}
          valueLabel="Chats Atendidos"
        />
      </div>
    </div>
  );
}
